/**
 * Firebase TOTP MFA 薄ラッパ (#815 / #789 decision).
 *
 * WebAuthn 不可層 (#787) の fallback 認証 = Firebase Authentication with
 * Identity Platform の TOTP 第2要素。`lib/googleAuth.ts` が auth を wrap する
 * のと同方針で、`firebase/auth` の MFA modular API をここに集約する。
 *
 * 前提 (docs/infra-manual-operations.md §17 / 起票 #815):
 * - project round-trip-320615 が Identity Platform にアップグレード済
 * - TOTP provider が ENABLED (infra/scripts/enable-totp-mfa.py)
 * - email 検証済 (Google sign-in は email_verified=true 自動付与で充足)
 *
 * 強度は #788 ①'②③④ (demo 実証済) が担保するため、本 fallback は
 * 「ゆるい identity 継続性」で足りる (過剰実装しない / #789)。
 */
import {
  multiFactor,
  TotpMultiFactorGenerator,
  getMultiFactorResolver,
  reauthenticateWithPopup,
  GoogleAuthProvider,
  type User,
  type MultiFactorError,
  type MultiFactorResolver,
  type TotpSecret,
  type UserCredential,
} from 'firebase/auth'
import { auth } from './firebase'

const APP_NAME = 'YOKOSO PAY'

/** Firebase が「この sign-in は MFA 必須」を示す error か。 */
export function isMfaRequiredError(e: unknown): e is MultiFactorError {
  return (
    typeof e === 'object' &&
    e !== null &&
    (e as { code?: string }).code === 'auth/multi-factor-auth-required'
  )
}

/** user に TOTP 第2要素が enroll 済か。 */
export function hasTotpFactor(user: User | null): boolean {
  if (!user) return false
  return multiFactor(user).enrolledFactors.some(
    (f) => f.factorId === TotpMultiFactorGenerator.FACTOR_ID,
  )
}

export interface TotpEnrollStart {
  secret: TotpSecret
  /** authenticator アプリ用 otpauth:// URL (QR 化して表示)。 */
  qrCodeUrl: string
  /** QR をスキャンできない旧 device 向けの手入力キー。 */
  manualKey: string
}

/**
 * TOTP enroll 開始: secret 生成 + QR/手入力キーを返す。
 * (ユーザーが authenticator アプリに登録 → 6 桁コードを completeTotpEnroll へ)
 */
export async function startTotpEnroll(
  user: User,
  appName: string = APP_NAME,
): Promise<TotpEnrollStart> {
  const session = await multiFactor(user).getSession()
  const secret = await TotpMultiFactorGenerator.generateSecret(session)
  const email = user.email || appName
  return {
    secret,
    qrCodeUrl: secret.generateQrCodeUrl(email, appName),
    manualKey: secret.secretKey,
  }
}

/** TOTP enroll 完了: アプリの 6 桁コードで検証して factor を登録。 */
export async function completeTotpEnroll(
  user: User,
  secret: TotpSecret,
  code: string,
  displayName = 'YOKOSO PAY TOTP',
): Promise<void> {
  const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, code)
  await multiFactor(user).enroll(assertion, displayName)
}

/**
 * MFA 必須 sign-in error から TOTP resolver を取得。
 * TOTP hint が無ければ null (= TOTP 未 enroll の MFA = 想定外)。
 */
export function getTotpResolver(error: MultiFactorError): MultiFactorResolver | null {
  const resolver = getMultiFactorResolver(auth, error)
  const hasTotpHint = resolver.hints.some(
    (h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID,
  )
  return hasTotpHint ? resolver : null
}

/**
 * resolver + 6 桁コードで sign-in を解決。成功で UserCredential を返すので
 * 呼び元 (Login.tsx) は通常の finishFirebaseLogin に合流できる。
 */
export async function resolveTotpSignIn(
  resolver: MultiFactorResolver,
  otpCode: string,
): Promise<UserCredential> {
  const hint = resolver.hints.find(
    (h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID,
  )
  if (!hint) throw new Error('no TOTP factor on this account')
  const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, otpCode)
  return resolver.resolveSignIn(assertion)
}

/**
 * Google 再認証 (#1107: MFA 変更等の `auth/requires-recent-login` 対策)。
 *
 * Firebase は MFA factor の解除のようなセンシティブ操作に「最近のログイン」を
 * 要求する。Google popup で再認証するが、MFA(TOTP)enroll 済みだと再認証自体が
 * `auth/multi-factor-auth-required` を投げるため、その resolver を返して呼び元が
 * TotpSignInModal で解決 → `resolveSignIn` で再認証完了させる二段構え。
 *
 * Returns:
 *   - `{ ok: true }`: 再認証完了(recent-login 更新済) → 呼び元は元操作を retry
 *   - `{ ok: false, resolver }`: MFA 必須 → 呼び元は TotpSignInModal で解決後 retry
 * それ以外の失敗(popup closed 等)は throw。
 */
export async function reauthenticateGoogle(
  user: User,
): Promise<{ ok: true } | { ok: false; resolver: MultiFactorResolver }> {
  try {
    await reauthenticateWithPopup(user, new GoogleAuthProvider())
    return { ok: true }
  } catch (e) {
    if (isMfaRequiredError(e)) {
      return { ok: false, resolver: getMultiFactorResolver(auth, e) }
    }
    throw e
  }
}

/**
 * TOTP factor を解除 (設定画面での再登録 / in-session recovery)。
 * 紛失で sign-in 不能な full lockout は operator 補助 (Admin SDK unenroll、
 * docs/infra-manual-operations.md §17) — self-service 復旧コードは作らない (#789)。
 */
export async function unenrollTotp(user: User): Promise<void> {
  const totp = multiFactor(user).enrolledFactors.find(
    (f) => f.factorId === TotpMultiFactorGenerator.FACTOR_ID,
  )
  if (totp) await multiFactor(user).unenroll(totp)
}
